<template>
    <div class="container py-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <h3 class="mb-1">Cancellation Requests</h3>
                <p class="text-muted mb-0">Review and approve order cancellation requests.</p>
            </div>
            <b-button variant="primary" @click="fetchRequests">Refresh</b-button>
        </div>

        <b-card>
            <b-table :items="requests" :fields="fields" :busy="loading" responsive small>
                <template #cell(target_type)="data">
                    <span class="badge badge-info">{{ data.item.target_type }}</span>
                </template>
                <template #cell(entities)="data">
                    <div class="small">
                        <div>
                            <strong>Cart:</strong>
                            {{ entityLabel(data.item, "cart") }}
                        </div>
                        <div>
                            <strong>Bill:</strong>
                            {{ entityLabel(data.item, "bill") }}
                        </div>
                        <div>
                            <strong>Delivery Order:</strong>
                            {{ entityLabel(data.item, "delivery_order_item") }}
                        </div>
                    </div>
                </template>
                <template #cell(reason)="data">
                    <div style="max-width: 340px">{{ data.item.reason }}</div>
                </template>
                <template #cell(requested_by)="data">
                    {{ data.item.requested_by && data.item.requested_by.name }}
                </template>
                <template #cell(status)="data">
                    <span :class="['badge', data.item.status === 'pending' ? 'badge-warning' : data.item.status === 'approved' ? 'badge-success' : 'badge-danger']">
                        {{ data.item.status }}
                    </span>
                </template>
                <template #cell(actions)="data">
                    <div class="d-flex">
                        <b-button
                            size="sm"
                            variant="success"
                            class="mr-2"
                            :disabled="data.item.status !== 'pending' || !canApprove"
                            @click="openReviewModal(data.item, 'approve')"
                        >
                            Approve
                        </b-button>
                        <b-button
                            size="sm"
                            variant="danger"
                            :disabled="data.item.status !== 'pending' || !canReject"
                            @click="openReviewModal(data.item, 'reject')"
                        >
                            Reject
                        </b-button>
                    </div>
                </template>
            </b-table>
        </b-card>

        <b-modal
            v-model="showReviewModal"
            centered
            hide-footer
            title="Cancellation Request Review"
            body-class="p-0"
        >
            <div class="review-modal-shell">
                <div class="review-modal-header">
                    <div>
                        <div class="review-modal-eyebrow">Cancellation Review</div>
                        <h5 class="mb-1">
                            {{ pendingReviewAction === "approve" ? "Approve Request" : "Reject Request" }}
                        </h5>
                        <p class="mb-0 text-muted">
                            Please enter a reason before submitting this review.
                        </p>
                    </div>
                    <span class="badge badge-pill badge-light">
                        {{ pendingReviewRequest.target_type || "request" }}
                    </span>
                </div>

                <div class="review-modal-body">
                    <div class="mb-3">
                        <label class="font-weight-bold">Original Request Reason</label>
                        <div class="form-control review-readonly">
                            {{ pendingReviewRequest.reason || "No reason provided" }}
                        </div>
                    </div>
                    <div class="mb-3">
                        <label class="font-weight-bold" for="reviewReasonInput">
                            Reason for {{ pendingReviewAction === "approve" ? "approval" : "rejection" }}
                        </label>
                        <textarea
                            id="reviewReasonInput"
                            v-model.trim="reviewReason"
                            class="form-control"
                            rows="4"
                            :placeholder="
                                pendingReviewAction === 'approve'
                                    ? 'Enter approval note'
                                    : 'Enter rejection reason'
                            "
                        ></textarea>
                    </div>

                    <div class="d-flex justify-content-end">
                        <b-button variant="outline-secondary" class="mr-2" @click="closeReviewModal">
                            Cancel
                        </b-button>
                        <b-button
                            :variant="pendingReviewAction === 'approve' ? 'success' : 'danger'"
                            :disabled="reviewSubmitting || !reviewReason"
                            @click="submitReview"
                        >
                            <span
                                v-if="reviewSubmitting"
                                class="spinner-border spinner-border-sm mr-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            {{ pendingReviewAction === "approve" ? "Approve" : "Reject" }}
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from "axios"
import { mapGetters } from "vuex"

export default {
    name: "CancellationRequests",
    data() {
        return {
            requests: [],
            loading: false,
            showReviewModal: false,
            reviewSubmitting: false,
            pendingReviewRequest: {},
            pendingReviewAction: "approve",
            reviewReason: "",
            fields: [
                { key: "target_type", label: "Type" },
                { key: "entities", label: "Associated Entities" },
                { key: "reason", label: "Reason" },
                { key: "requested_by", label: "Requested By" },
                { key: "status", label: "Status" },
                { key: "createdAt", label: "Created" },
                { key: "actions", label: "Action" },
            ],
        }
    },
    computed: {
        ...mapGetters({
            accessLevel: "Admin/Auth/getAccessLevel",
            specialAccess: "Admin/Auth/getSpecialAccess",
        }),
        canModerate() {
            return (
                this.accessLevel === 3 ||
                (this.specialAccess || []).some(
                    a => a.access_name === "cancel_order_requests"
                ) ||
                (this.specialAccess || []).some(
                    a => a.access_name === "validate_cancel_request"
                )
            )
        },
        canReject() {
            return this.canModerate
        },
        canApprove() {
            return this.canModerate
        },
    },
    mounted() {
        this.fetchRequests()
    },
    methods: {
        async fetchRequests() {
            this.loading = true
            try {
                const { data } = await axios.get("/cancellation-requests/query", {
                    params: {
                        query: JSON.stringify({}),
                        skip: 0,
                        count: 100,
                    },
                })
                this.requests = data.items || []
            } finally {
                this.loading = false
            }
        },
        openReviewModal(request, action) {
            this.pendingReviewRequest = request || {}
            this.pendingReviewAction = action
            this.reviewReason = ""
            this.showReviewModal = true
        },
        closeReviewModal() {
            this.showReviewModal = false
            this.pendingReviewRequest = {}
            this.pendingReviewAction = "approve"
            this.reviewReason = ""
        },
        async submitReview() {
            if (!this.pendingReviewRequest || !this.pendingReviewRequest._id) return
            this.reviewSubmitting = true
            try {
                const endpoint =
                    this.pendingReviewAction === "approve"
                        ? "/cancellation-requests/approve"
                        : "/cancellation-requests/reject"
                await axios.put(endpoint, {
                    id: this.pendingReviewRequest._id,
                    review_reason: this.reviewReason,
                })
                this.closeReviewModal()
                await this.fetchRequests()
            } finally {
                this.reviewSubmitting = false
            }
        },
        entityLabel(request, key) {
            const id =
                request.associated_entities &&
                request.associated_entities[key]
                    ? request.associated_entities[key]
                    : request[`${key}_id`]
            if (!id) return "Not linked"
            return typeof id === "object" && id._id ? id._id : String(id)
        },
    },
}
</script>

<style scoped>
.review-modal-shell {
    padding: 1.25rem;
}

.review-modal-header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.review-modal-eyebrow {
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #6c757d;
    margin-bottom: 0.25rem;
}

.review-modal-body {
    padding-top: 1rem;
}

.review-readonly {
    min-height: 90px;
    background: #f8f9fa;
    white-space: pre-wrap;
}
</style>
